import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import videojs from 'video.js';

import { BlobModel } from '../../../../models/blob.model';
import { RolesEnum } from '../../../../models/enums/roles.enum';
import { DictionaryType } from '../../../../models/types/dictionary.type';
import { BlobsResource } from '../../../../services/resources/blobs.resource';
import { TranslationService } from '../../../../services/translation.service';
import { UserSessionService } from '../../../../services/user-session.service';

@Component({
    selector: 'sl-live-stream-video',
    templateUrl: './live-stream-video.component.html',
    styleUrls: ['./live-stream-video.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LiveStreamVideoComponent implements OnInit {
    @ViewChild('streamPlayer') playerEl: ElementRef;

    isSaving = false;
    isLoading = true;
    isEditing = false;
    canEdit = false;
    allowedLangCodes: string[] = [];
    currentData: DictionaryType<string>;
    formGroup: UntypedFormGroup;
    player: any;

    private streamUrl: string;
    private streamKey = 'live-stream-settings';

    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly userSessionService = inject(UserSessionService);
    private readonly translationService = inject(TranslationService);
    private readonly blobsResource = inject(BlobsResource);

    ngOnInit(): void {
        this.isLoading = true;
        this.canEdit = this.userSessionService.roleId > RolesEnum.Netvote;
        const formConfig: any = {};

        this.translationService.allowedLanguages.forEach(l => this.allowedLangCodes.push(l.code));
        this.allowedLangCodes.forEach(c => (formConfig[c] = [undefined, undefined]));

        this.formGroup = this.formBuilder.group(formConfig);

        this.blobsResource
            .download(this.streamKey)
            .pipe(
                catchError(() => {
                    this.isLoading = false;
                    return of({ value: '' });
                })
            )
            .subscribe(content => {
                if (!!content.value) {
                    this.currentData = JSON.parse(atob(content.value));
                    this.streamUrl = this.currentData[this.translationService.getCurrent().code] ?? '';

                    this.formGroup.patchValue(this.currentData);
                    this.startPlayer();
                }

                this.isLoading = false;
            });
    }

    save(): void {
        this.isSaving = true;
        this.currentData = this.formGroup.value;
        this.streamUrl = this.currentData[this.translationService.getCurrent().code] ?? '';

        this.blobsResource
            .upload(
                new BlobModel({
                    key: this.streamKey,
                    data: btoa(JSON.stringify(this.currentData)),
                    mimeType: 'text/plain'
                })
            )
            .subscribe(() => {
                this.isSaving = false;
                this.isEditing = false;

                this.startPlayer();
            });
    }

    getCode(code: string): string {
        return code.charAt(0).toUpperCase() + code.slice(1);
    }

    private startPlayer(): void {
        this.player = videojs(this.playerEl.nativeElement, {
            autoplay: true,
            controls: true,
            sources: [{ src: this.streamUrl }]
        });
    }
}
